import { ref, type Ref } from 'vue';
import api from '@/api';
import type { ReferralTypeDto } from '@/api/backend/public/referral-types/referral-type.dto';
import type { CustomerAwareOfBridgit } from '@/types/api/api-enums';

export type UtmMetricRecord = {
  id?: string;
  applicationId?: string;
  referralTypeId?: string;
  referralOtherDescription?: string;
  originatorType?: 'broker' | 'referrer';
  originatorId?: string;
  customerAwareOfBridgit?: CustomerAwareOfBridgit | null;
};

export type MarketingFormData = {
  referralTypeId?: string;
  referralOtherDescription?: string;
  customerAwareOfBridgit?: CustomerAwareOfBridgit | null;
};

export function useMarketing() {
  const brokerMarketingData: Ref<UtmMetricRecord | null> = ref(null);
  const referralTypes: Ref<ReferralTypeDto[]> = ref([]);
  const formData: Ref<MarketingFormData> = ref({});

  /**
   * Fetches broker marketing data from utm_metrics table
   * Relies on broker authentication context
   */
  const fetchBrokerMarketingData = async (): Promise<UtmMetricRecord | null> => {
    try {
      const response = await api.backend.brokers.utmMetrics.retrieveUtmMetrics();
      brokerMarketingData.value = response.data.utmMetric || null;
      return brokerMarketingData.value;
    } catch (error) {
      console.error('Failed to fetch broker marketing data:', error);
      return null;
    }
  };

  /**
   * Fetches referral types for a specific display context and origin type
   * @param displayContext - The display context (broker or customer)
   * @param originType - The origin type (e.g., broker white-label type)
   * @param brokerId - Optional broker ID (only used for broker context validation)
   */
  const fetchReferralTypes = async (
    displayContext: 'broker' | 'customer' = 'customer',
    originType?: string,
    brokerId?: string,
  ): Promise<ReferralTypeDto[]> => {
    // For broker context, require brokerId
    if (displayContext === 'broker' && !brokerId) {
      referralTypes.value = [];
      return [];
    }

    try {
      const response = await api.backend.public.listReferralTypes({
        displayContext,
        originType,
      });

      referralTypes.value = response.data.referralTypes || [];
      return referralTypes.value;
    } catch (error) {
      console.error('Failed to fetch referral types:', error);
      referralTypes.value = [];
      return [];
    }
  };

  /**
   * Sets the marketing form data
   * @param data - The marketing form data to set
   */
  const setMarketingFormData = (data: MarketingFormData): void => {
    formData.value = { ...formData.value, ...data };
  };

  /**
   * Resets the marketing store state
   */
  const reset = (): void => {
    brokerMarketingData.value = null;
    referralTypes.value = [];
    formData.value = {};
  };

  return {
    // State
    brokerMarketingData,
    referralTypes,
    formData,
    // Actions
    fetchBrokerMarketingData,
    fetchReferralTypes,
    setMarketingFormData,
    reset,
  };
}
